import { useState, useRef, useCallback, useEffect, forwardRef, useImperativeHandle } from "react"
import { api } from "aldoo-ra/API"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { ArrowUpTrayIcon, LinkIcon } from "@heroicons/react/24/outline"
import Modal from "aldoo-ra/Modal"

const MIN_SEL = 20
const MAX_FILE_SIZE = 10 * 1024 * 1024

const HANDLES = [
  { id: "nw", style: { top: -6, left: -6 },                              cursor: "nw-resize" },
  { id: "n",  style: { top: -6, left: "calc(50% - 5px)" },              cursor: "n-resize"  },
  { id: "ne", style: { top: -6, right: -6 },                             cursor: "ne-resize" },
  { id: "e",  style: { top: "calc(50% - 5px)", right: -6 },             cursor: "e-resize"  },
  { id: "se", style: { bottom: -6, right: -6 },                          cursor: "se-resize" },
  { id: "s",  style: { bottom: -6, left: "calc(50% - 5px)" },           cursor: "s-resize"  },
  { id: "sw", style: { bottom: -6, left: -6 },                           cursor: "sw-resize" },
  { id: "w",  style: { top: "calc(50% - 5px)", left: -6 },              cursor: "w-resize"  },
]

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v))

const CropEditor = forwardRef(function CropEditor({ src }, ref) {
  const containerRef = useRef(null)
  const imgRef = useRef(null)
  const [imgBounds, setImgBounds] = useState(null)
  const [sel, setSel] = useState(null)
  const dragRef = useRef(null)

  const measureImg = useCallback(() => {
    const img = imgRef.current
    const cont = containerRef.current
    if (!img || !cont || !img.naturalWidth) return
    const iR = img.getBoundingClientRect()
    const cR = cont.getBoundingClientRect()
    const b = { x: iR.left - cR.left, y: iR.top - cR.top, w: iR.width, h: iR.height }
    setImgBounds(b)
    setSel({ ...b })
  }, [])

  useEffect(() => {
    window.addEventListener("resize", measureImg)
    return () => window.removeEventListener("resize", measureImg)
  }, [measureImg])

  useImperativeHandle(ref, () => ({
    crop: () => new Promise((resolve, reject) => {
      const img = imgRef.current
      if (!img || !imgBounds || !sel) return reject(new Error("No image"))
      const scaleX = img.naturalWidth / imgBounds.w
      const scaleY = img.naturalHeight / imgBounds.h
      const sx = Math.round((sel.x - imgBounds.x) * scaleX)
      const sy = Math.round((sel.y - imgBounds.y) * scaleY)
      const sw = Math.max(1, Math.round(sel.w * scaleX))
      const sh = Math.max(1, Math.round(sel.h * scaleY))
      const canvas = document.createElement("canvas")
      canvas.width = sw
      canvas.height = sh
      try {
        canvas.getContext("2d").drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh)
        canvas.toBlob(resolve, "image/webp", 0.88)
      } catch (err) {
        reject(err)
      }
    }),
  }))

  const toLocal = (e) => {
    const cR = containerRef.current.getBoundingClientRect()
    return { x: e.clientX - cR.left, y: e.clientY - cR.top }
  }

  const startDrag = useCallback((e, handle) => {
    e.preventDefault()
    e.stopPropagation()
    dragRef.current = { handle, start: toLocal(e), orig: { ...sel } }
    e.currentTarget.setPointerCapture(e.pointerId)
  }, [sel])

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current
    if (!d || !imgBounds) return
    const pos = toLocal(e)
    const dx = pos.x - d.start.x
    const dy = pos.y - d.start.y
    let { x, y, w, h } = d.orig

    if (d.handle === "move") {
      setSel({
        x: clamp(x + dx, imgBounds.x, imgBounds.x + imgBounds.w - w),
        y: clamp(y + dy, imgBounds.y, imgBounds.y + imgBounds.h - h),
        w, h,
      })
      return
    }

    if (d.handle.includes("e")) w = clamp(w + dx, MIN_SEL, imgBounds.x + imgBounds.w - x)
    if (d.handle.includes("s")) h = clamp(h + dy, MIN_SEL, imgBounds.y + imgBounds.h - y)
    if (d.handle.includes("w")) {
      const nx = clamp(x + dx, imgBounds.x, x + w - MIN_SEL)
      w = w + x - nx; x = nx
    }
    if (d.handle.includes("n")) {
      const ny = clamp(y + dy, imgBounds.y, y + h - MIN_SEL)
      h = h + y - ny; y = ny
    }
    setSel({ x, y, w, h })
  }, [imgBounds])

  return (
    <div
      ref={containerRef}
      className="relative bg-neutral-900 select-none rounded-xl overflow-hidden"
      style={{ height: 360 }}
      onPointerMove={onPointerMove}
      onPointerUp={() => { dragRef.current = null }}
      onPointerLeave={() => { dragRef.current = null }}
    >
      <img
        ref={imgRef}
        src={src}
        onLoad={measureImg}
        crossOrigin={src?.startsWith("data:") ? undefined : "anonymous"}
        draggable={false}
        alt=""
        className="max-w-full max-h-full absolute inset-0 m-auto block pointer-events-none select-none"
      />
      {sel && (
        <div
          className="absolute border border-white cursor-move"
          style={{
            left: sel.x, top: sel.y,
            width: sel.w, height: sel.h,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.6)",
            zIndex: 1,
            touchAction: "none",
          }}
          onPointerDown={(e) => startDrag(e, "move")}
        >
          <div className="absolute inset-0 pointer-events-none">
            {[33.33, 66.67].map((p) => (
              <div key={`v${p}`} className="absolute top-0 bottom-0 border-l border-white/20" style={{ left: `${p}%` }} />
            ))}
            {[33.33, 66.67].map((p) => (
              <div key={`h${p}`} className="absolute left-0 right-0 border-t border-white/20" style={{ top: `${p}%` }} />
            ))}
          </div>
          {HANDLES.map(({ id, style, cursor }) => (
            <div
              key={id}
              className="absolute w-3 h-3 bg-white rounded-sm shadow"
              style={{ ...style, cursor, position: "absolute", touchAction: "none" }}
              onPointerDown={(e) => startDrag(e, id)}
            />
          ))}
        </div>
      )}
    </div>
  )
})

export default function AddMediaDialog({ onUse, onCancel, imagesOnly = false, uploadFn, currentImageUrl = null }) {
  const texts = TextBundle({ id: "add_media_dialog" })
  const [phase, setPhase] = useState(currentImageUrl ? "crop" : "drop")
  const [imageSrc, setImageSrc] = useState(currentImageUrl || null)
  const [urlInput, setUrlInput] = useState("")
  const [dragging, setDragging] = useState(false)
  const [error, setError] = useState("")
  const cropRef = useRef(null)
  const fileInputRef = useRef(null)

  useEffect(() => {
    const onPaste = (e) => {
      if (phase === "uploading") return
      const items = e.clipboardData?.items
      if (!items) return
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          openImage(URL.createObjectURL(item.getAsFile()))
          return
        }
        if (item.type === "text/plain" && phase === "drop") {
          item.getAsString((text) => {
            const t = text.trim()
            if (/^https?:\/\//i.test(t)) setUrlInput(t)
          })
        }
      }
    }
    window.addEventListener("paste", onPaste)
    return () => window.removeEventListener("paste", onPaste)
  }, [phase])

  if (!texts) return null

  const openImage = (src) => {
    setImageSrc(src)
    setError("")
    setPhase("crop")
  }

  const processFile = (file) => {
    if (!file) return
    if (file.size > MAX_FILE_SIZE) { setError(texts.sizeError); return }
    if (file.type.startsWith("image/")) return openImage(URL.createObjectURL(file))
    setError(texts.unsupportedTypeErrorImageOnly)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragging(false)
    processFile(e.dataTransfer.files[0])
  }

  const handleLoadUrl = () => {
    const url = urlInput.trim()
    if (url) openImage(url)
  }

  const goBack = () => {
    setPhase("drop")
    setImageSrc(null)
    setError("")
  }

  const handleUse = async () => {
    setError("")
    const prevPhase = phase
    setPhase("uploading")
    try {
      let file, mimeType
      try {
        file = await cropRef.current.crop()
      } catch {
        throw new Error(texts.cropError)
      }
      mimeType = "image/webp"
      const result = await uploadFn(file, mimeType)
      if (result?.error) throw new Error(result.error)
      onUse(result)
    } catch (err) {
      setError(err.message || texts.uploadFailTryAgainError)
      setPhase(prevPhase)
    }
  }

  const footerContent = phase === "crop" ? (
    <div className="flex gap-3 px-6 pb-6 pt-2">
      <Button variant="secondary" onClick={goBack} className="flex-1">
        {currentImageUrl ? texts.replaceButton : texts.backButton}
      </Button>
      <Button onClick={handleUse} className="flex-1">
        {texts.useButton}
      </Button>
    </div>
  ) : null

  return (
    <Modal
      isOpen={true}
      onClose={onCancel}
      variant="centered"
      className="max-w-lg"
      title={texts.dialogTitle}
      footerContent={footerContent}
    >

      {phase === "drop" && (
        <div className="flex flex-col gap-4">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed cursor-pointer transition py-12
              ${dragging ? "border-primary bg-primary/10" : "border-bordered hover:border-primary"}`}
          >
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition ${dragging ? "bg-primary/20" : "bg-neutral"}`}>
              <ArrowUpTrayIcon className={`w-6 h-6 transition ${dragging ? "text-primary" : "text-text_secondary"}`} />
            </div>
            <div className="text-center">
              <Typography variant="txt_dim" className="block font-semibold mb-4">
                {texts.dropZoneTitle}
              </Typography>
              <Typography variant="txt2_dim" className="block text-neutral_2 mt-1">
                {texts.pasteWith}{" "}
                <kbd className="px-1 py-0.5 bg-neutral border border-bordered rounded text-[11px] font-mono">
                  {texts.keyboardPaste}
                </kbd>
                {" "}{texts.keyboardUpload}
              </Typography>
            </div>
          </div>

          <Input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => { processFile(e.target.files?.[0]); e.target.value = "" }}
          />

          <div className="flex gap-2">
            <Input
              variant="pulse_compact"
              icon={<LinkIcon className="w-4 h-4 text-text_secondary shrink-0" />}
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleLoadUrl()}
              placeholder={texts.urlPlaceholder}
              className="flex-1"
            />
            <Button variant="primary" onClick={handleLoadUrl} disabled={!urlInput.trim()} className="disabled:opacity-50">
              {texts.loadButton}
            </Button>
          </div>

          {error && <p className="text-sm text-error">{error}</p>}
        </div>
      )}

      {phase === "crop" && imageSrc && (
        <div className="flex flex-col gap-3">
          <CropEditor ref={cropRef} src={imageSrc} />
          <Typography variant="txt1_dim" className="text-center">
            {texts.cropEditorText}
          </Typography>
          {error && <p className="text-sm text-error">{error}</p>}
        </div>
      )}

      {phase === "uploading" && (
        <div className="flex flex-col items-center justify-center gap-4 py-16">
          <div className="w-10 h-10 border-2 border-primary/10 border-t-primary rounded-full animate-spin" />
          <Typography variant="txt1_dim" className="block">
            {texts.uploadingButton}
          </Typography>
        </div>
      )}
    </Modal>
  )
}